import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import { store } from '@/store';
import ProductTileColorBaubles from '@/ui/shared/product-tile/ProductTileColorBaubles.vue';
import ProductTileImageCarousel from '@/ui/shared/product-tile/ProductTileImageCarousel.vue';
import VariantColorViewObject = Vertica.LouisPoulsen.Application.Content.Pages.Product.VariantColorViewObject;
import { QueryParams } from '@/core/query-params.service';
import userAgent from '@/services/userAgent.service';
import { ProductTileView } from '@/enums';

export interface ProductTileImage {
    imageUrl: string;
    view: ProductTileView;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductTile',
  props: {
    model: null,
    isTallProduct: { type: Boolean, default: false },
    isSearchTile: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { model: ProductTileViewModel, isTallProduct: boolean, isSearchTile: boolean }



const theme = computed<ColorTheme>(() => store.getters['app/theme']);
const isPrivateMode = computed<boolean>(() => store.getters['app/isPrivateMode']);
const productTileView = computed<ProductTileView>(() => store.getters['catalog/productTileView']);
const selectedColorFilters = computed<string[]>(() => store.getters['catalog/productTileColorFilters']);

const runImageAnimations = ref<boolean>(userAgent.isPrerender);
const inView = ref<boolean>(userAgent.isPrerender);
const imageReady = ref<boolean>(false);
const isImageErrored = ref<boolean>(false);
const showSpinner = ref<boolean>(false);
const initOverlayAnimations = ref<boolean>(false);
const selectedColor = ref<VariantColorViewObject>(null);

onMounted(() => {
    setSelectedColorByFilterIfAny();
});

function setSelectedColorByFilterIfAny() {
    selectedColor.value = null;
    const filters = selectedColorFilters.value;
    const variants = props.model.colorScheme;

    // The filters are listed in the order they were selected. Eg. green, yellow, blue.
    // We loop over the filters and if a variant match the current filter, e.g. green, we select it.
    // If not, we continue with the next filter, in this case yellow, and repeat.
    filters.some(filter => {
        const result = variants.some(variant => {
            if (variant.colors.includes(filter)) {
                selectedColor.value = variant;
                return true;
            }
            return false;
        });
        return result;
    });

    // No filters were selected or the variants did not match the filters.
    if (!selectedColor.value) selectedColor.value = props.model.colorScheme[0];
}

const packshot = computed<ProductTileImage | null>(() => selectedColor.value ? { imageUrl: selectedColor.value.imageUrl, view: ProductTileView.Product } : null);
const roomImages = computed<ProductTileImage[]>(() => {
    if (!selectedColor.value) { return []; }
    const inspirationImages = (props.model.inspirationImages || []).find(x => x.variantId === selectedColor.value.variantId);
    return inspirationImages ? inspirationImages.imageUrls.map(imageUrl => ({ imageUrl, view: ProductTileView.Room })) : [];
});

const allImages = computed<ProductTileImage[]>(() => {
    const images = roomImages.value.concat();
    if (selectedColor.value) {
        images.unshift(packshot.value);
    }
    return images;
});

const visibleImages = computed<ProductTileImage[]>(() => {
    if (inView.value) {
        const view = productTileView.value;
        if (view === ProductTileView.Product) {
            return packshot.value ? [packshot.value, ...roomImages.value] : [];
        }
        if (view === ProductTileView.Room) {
            return roomImages.value;
        }
        return allImages.value;
    }
    return [];
});

const isEmptyProduct = computed<boolean>(() => !props.model.productId);

const hasImage = computed<boolean>(() => !!allImages.value.length);

const getUrl = computed<string>(() => {
    if (selectedColor.value && selectedColor.value !== props.model.colorScheme[0] && selectedColor.value.variantId) {
        let modelUrl = props.model.url;

        const modelIsVariantIndex = props.model.url.indexOf('?v=');
        if (modelIsVariantIndex !== -1) {
            modelUrl = props.model.url.slice(0, modelIsVariantIndex);
        }

        const variantUrl = `${modelUrl}?${QueryParams.SelectedVariantId}=${selectedColor.value.variantId}`;
        return variantUrl;
    }

    return props.model.url;
});

const hasColorVariants = computed<boolean>(() =>
    // Checks if every color in "colorScheme" has a hex value, by checking that the value is not "null", "undefined" or "''" using "!!"
    props.model.colorScheme && props.model.colorScheme.every(color => !!color.hex));

const backgroundColor = computed<string>(() => {
    return theme.value.productTileBackgroundColor ? theme.value.productTileBackgroundColor : props.model.backgroundColor;
});

function setInView() {
    inView.value = true;
    if (!allImages.value.length) {
        // Animate the "no-image" if the product does not have an image
        runImageAnimations.value = true;
    }
    delayPreloadSpinner();
};

function imageLoaded() {
    imageReady.value = true;
    isImageErrored.value = false;
    if (inView.value) {
        initAnimations();
    }
}

function imageLoadError() {
    imageReady.value = true;
    isImageErrored.value = true;

    if (inView.value) {
        initAnimations();
    }
}

function initAnimations() {
    runImageAnimations.value = true;
}

function delayPreloadSpinner() {
    setTimeout(() => {
        showSpinner.value = true; // Delay show spinner if images hasn't loaded after 300ms
    }, 300);
}

function setSelectedColor(color: VariantColorViewObject): void {
    if (selectedColor.value.hex === color.hex) {
        return;
    }
    imageReady.value = false;
    selectedColor.value = color;
}

return { __sfc: true,props, theme, isPrivateMode, productTileView, selectedColorFilters, runImageAnimations, inView, imageReady, isImageErrored, showSpinner, initOverlayAnimations, selectedColor, setSelectedColorByFilterIfAny, packshot, roomImages, allImages, visibleImages, isEmptyProduct, hasImage, getUrl, hasColorVariants, backgroundColor, setInView, imageLoaded, imageLoadError, initAnimations, delayPreloadSpinner, setSelectedColor, ProductTileColorBaubles, ProductTileImageCarousel }
}

})